import update from "immutability-helper";
import { createSelector } from "reselect";

import { type ApiError, type IEntryType, type IGuestAddingState, type IGuestlistDeletionState, type IGuestlistSettingsState, type IGuestlistState, type IGuestsState, type IModals, type IPlaceManagementState, type IRow, type ISearchState } from "../../guestlist/model";

const getGuestsState = (state: IGuestlistState): IGuestsState => state.guestsState;

const getPermissions = (state: IGuestlistState): server.dto.LegacyGuestlistPermission => state.permission;

const getModals = (state: IGuestlistState): IModals => state.modals;

const getSearchState = (state: IGuestlistState): ISearchState => state.searchState;

const getPlaceManagementState = createSelector<IGuestlistState, IModals, IPlaceManagementState>(
  getModals,
  (modals) => modals.placeManagementState
);

const getGuestlistSettingsState = createSelector<IGuestlistState, IModals, IGuestlistSettingsState>(
  getModals,
  (modals) => modals.guestlistSettingsState
);

const getGuestlistAddingState = createSelector<IGuestlistState, IModals, IGuestAddingState>(
  getModals,
  (modals) => modals.guestAddingState
);

const getGuestlistDeletionState = createSelector<IGuestlistState, IModals, IGuestlistDeletionState>(
  getModals,
  (modals) => modals.guestlistDeletionState
);

export const getGuestlistDeletionIsShown = createSelector<IGuestlistState, IGuestlistDeletionState, boolean>(
  getGuestlistDeletionState,
  (state) => state.showModal
);

export const getGuestlist = (state: IGuestlistState): server.dto.LegacyGuestlist => state.guestlist;

export const getIsLoading = (state: IGuestlistState): boolean => state.loading;

export const getHasData = (state: IGuestlistState): boolean => state.guestlist != null;

export const getHasError = (state: IGuestlistState): boolean => state.error;

export const getGuestCount = createSelector(getGuestlist, guestlist => guestlist?.availablePlaces.reduce((acc, place) => acc + place.usedPlaces, 0));

export const getGuestsHasError = createSelector<IGuestlistState, IGuestsState, boolean>(
  getGuestsState,
  (guestState) => guestState.error
);

export const getGuestlistSettingsModalIsShown = createSelector<IGuestlistState, IPlaceManagementState, boolean>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.showModal
);

export const getPlaceManagementIsLoading = createSelector<IGuestlistState, IPlaceManagementState, boolean>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.isLoading
);

export const getAddingModalIsShown = createSelector<IGuestlistState, IGuestAddingState, boolean>(
  getGuestlistAddingState,
  (guestlistAddingState) => guestlistAddingState.showModal
);

export const getAddingModalErrors = createSelector<IGuestlistState, IGuestAddingState, ApiError[]>(
  getGuestlistAddingState,
  (guestlistAddingState) => guestlistAddingState.errors
);

export const getAddingGuest = createSelector<IGuestlistState, IGuestAddingState, server.dto.LegacyGuest>(
  getGuestlistAddingState,
  (guestlistAddingState) => guestlistAddingState.editedGuest
);

export const shouldShowPlaceDeleteModal = createSelector<IGuestlistState, IPlaceManagementState, boolean>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.showDeleteModal
);

export const isGuestAddModalLoading = createSelector<IGuestlistState, IGuestAddingState, boolean>(
  getGuestlistAddingState,
  (guestlistAddingState) => guestlistAddingState.isLoading
);

export const getEntryTypes = (state: IGuestlistState): IEntryType[] => {
  const guestlist = getGuestlist(state);
  if (guestlist == null) {
    return [];
  }

  return guestlist.entryTypes;
};

export const getShowId = (state: IGuestlistState): number => state.showId;

export const getGuestlistSettingsErrors = createSelector<IGuestlistState, IGuestlistSettingsState, ApiError[]>(
  getGuestlistSettingsState,
  (guestlistSettingsState) => guestlistSettingsState.errors
);

export const getPlaceManagementErrors = createSelector<IGuestlistState, IPlaceManagementState, ApiError[]>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.errors
);

export const getCurrentEditedRow = createSelector<IGuestlistState, IPlaceManagementState, IRow>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.currentEdited
);

export const getGuestlistSettingsIsLoading = createSelector<IGuestlistState, IPlaceManagementState, boolean>(
  getPlaceManagementState,
  (placeManagementState) => placeManagementState.isLoading
);

export const getIsSearching = createSelector<IGuestlistState, ISearchState, boolean>(
  getSearchState,
  (searchState) => searchState.searchQuery !== ""
);

export const getSearchQuery = createSelector<IGuestlistState, ISearchState, string>(
  getSearchState,
  (searchState) => searchState.searchQuery
);

const getLowerCaseSearchQuery = createSelector<IGuestlistState, string, string>(
  getSearchQuery,
  (searchQuery) => searchQuery.toLowerCase()
);

export const getSearchResult = createSelector<IGuestlistState, string, server.dto.LegacyGuestlist, server.dto.LegacyGuestlistPlace[]>(
  getLowerCaseSearchQuery, getGuestlist,
  (searchQuery, guestlist) => {
    if (guestlist == null) {
      return [];
    }

    const places = guestlist.availablePlaces;
    const newPlaces = places.map((place) => update(place, {
      guests: {
        $set: place.guests.filter((guest) => {
          const fullName = `${guest.firstName} ${guest.lastName}`.toLowerCase();
          return fullName.includes(searchQuery);
        })
      }
    }));
    return newPlaces;
  }
);

export const canEditNoGuest = (): boolean => false;

export const canEditGuestlistSettings = createSelector<IGuestlistState, server.dto.LegacyGuestlistPermission, boolean>(
  getPermissions,
  (permissions) => permissions?.hasFullAccess
);

export const canEditAllGuests = createSelector<IGuestlistState, server.dto.LegacyGuestlistPermission, boolean>(
  getPermissions,
  (permissions) => permissions?.hasFullAccess
);

export const canEditOwnGuests = createSelector<IGuestlistState, server.dto.LegacyGuestlistPermission, boolean>(
  getPermissions,
  (permissions) => !permissions?.hasFullAccess
);
